/**
 * STABILITY-CRITICAL: marcador de "limpeza em andamento". Existir = o boot deve retomar (validar
 * sessão, pull base, seleção) em vez de abrir o app. Apagá-lo antes da hora deixa o aparelho
 * vazio sem explicação. Antes de alterar, leia docs/llm/slices/sync-engine.md e alinhe com o
 * responsável pelo sync.
 */
import { CLEANUP_MARKER_KEY } from './localStorageAllowlist'
import type { FlowEvent } from './flowEvents'

export type CleanupStep =
  'reset_started' | 'reset_done' | 'session_ok' | 'base_pulled' | 'downloading'
const ORDER: CleanupStep[] = [
  'reset_started',
  'reset_done',
  'session_ok',
  'base_pulled',
  'downloading',
]
export const stepIndex = (s: CleanupStep): number => ORDER.indexOf(s)

export type CleanupMarker = {
  v: 1
  step: CleanupStep
  startedAt: number
  userId: string
  savedFarmIds: string[]
  selectedFarmIds: string[] | null
  ownEvents: FlowEvent[]
}
type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const MINIMAL: CleanupMarker = {
  v: 1,
  step: 'reset_started',
  startedAt: 0,
  userId: '',
  savedFarmIds: [],
  selectedFarmIds: null,
  ownEvents: [],
}

export function readMarker(store: Store = localStorage): CleanupMarker | null {
  let raw: string | null
  try {
    raw = store.getItem(CLEANUP_MARKER_KEY)
  } catch {
    return null
  }
  if (raw === null) return null
  try {
    const m = JSON.parse(raw)
    if (m && m.v === 1 && ORDER.includes(m.step)) {
      return { ...MINIMAL, ...m }
    }
  } catch {
    /* corrompido */
  }
  return { ...MINIMAL } // chave existe mas ilegível: trata como em andamento (caminho seguro)
}

export function writeMarker(
  m: CleanupMarker,
  store: Store = localStorage,
): boolean {
  try {
    store.setItem(CLEANUP_MARKER_KEY, JSON.stringify(m))
    return true
  } catch {
    return false
  }
}

export function updateMarker(
  patch: Partial<CleanupMarker>,
  store: Store = localStorage,
): CleanupMarker | null {
  const cur = readMarker(store)
  if (!cur) return null
  const next = { ...cur, ...patch }
  return writeMarker(next, store) ? next : cur
}

export function clearMarker(store: Store = localStorage): void {
  try {
    store.removeItem(CLEANUP_MARKER_KEY)
  } catch {
    /* ignora */
  }
}

export const isCleanupActive = (store: Store = localStorage): boolean =>
  readMarker(store) !== null
